import { generateClient } from "aws-amplify/api";
import { updateOrgProfile as updateOrgProfileMutation } from "@/graphql/mutations";
import { parseAwsJson } from "@/utils/graphql";
import { getApiMessage } from "@/mixins/Extras/ApiResponse";
import {
  itemRowsToMobileStrings,
  itemRowsToStrings,
  isValidEmail,
  isValidMobile10,
} from "@/utils/agentConfiguration";

const client = generateClient();

export const ORG_TAG_LINE_MAX = 80;
export const ORG_DESCRIPTION_MAX = 300;

/** Fields allowed by GraphQL UpdateOrgProfileInput (see schema.json). */
export const UPDATE_ORG_PROFILE_FIELDS = [
  "org_name",
  "org_contact_number",
  "org_country_code",
  "org_address",
  "org_city",
  "org_state",
  "org_pincode",
  "org_logo_s3_key",
  "send_call_logs_by_email",
  "email_addresses",
  "send_call_logs_by_whatsapp",
  "whatsapp_numbers",
  "enable_webhook",
  "webhook_url",
  "webhook_auth_type",
  "webhook_auth_config",
  "org_tag_line",
  "org_description",
  "restrict_incoming_calls",
  "chatbot_details",
  "greeting_message",
  "system_prompt",
  "timezone",
  "disclaimer_enabled",
  "disclaimer_text",
  "business_hours_start",
  "business_hours_end",
  "out_of_hours_message",
  "send_dashboard_digest_by_email",
  "dashboard_digest_email_addresses",
  "send_dashboard_digest_by_whatsapp",
  "dashboard_digest_whatsapp_numbers",
  "dashboard_digest_cc_agents",
  "dashboard_digest_times",
  "dashboard_digest_weekly_enabled",
  "dashboard_digest_weekly_day",
  "dashboard_digest_weekly_time",
  "dashboard_digest_monthly_enabled",
  "dashboard_digest_monthly_day",
  "dashboard_digest_monthly_time",
];

function normalizeChatbotDetailsInput(value) {
  if (value == null || value === "") return undefined;
  if (typeof value === "string") return value;
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

/**
 * Strips unknown keys before calling updateOrgProfile.
 * @param {Record<string, unknown>} input
 */
export function sanitizeUpdateOrgProfileInput(input) {
  if (!input || typeof input !== "object") return {};

  const allowed = new Set(UPDATE_ORG_PROFILE_FIELDS);
  const sanitized = {};

  for (const [key, value] of Object.entries(input)) {
    if (!allowed.has(key) || value === undefined) continue;

    if (key === "chatbot_details") {
      const normalized = normalizeChatbotDetailsInput(value);
      if (normalized != null) sanitized[key] = normalized;
      continue;
    }

    if (
      key === "email_addresses" ||
      key === "whatsapp_numbers" ||
      key === "dashboard_digest_email_addresses" ||
      key === "dashboard_digest_whatsapp_numbers"
    ) {
      sanitized[key] = Array.isArray(value)
        ? value.map((item) => String(item || "").trim()).filter(Boolean)
        : [];
      continue;
    }

    sanitized[key] = value;
  }

  return sanitized;
}

export function validateOrgCallLogPreferences(form = {}) {
  if (form.send_call_logs_by_email) {
    const invalid = (form.email_address_rows || []).find(
      (row) => row.item_text?.trim() && !isValidEmail(row.item_text),
    );
    if (invalid) return "One or more additional email addresses are invalid.";
  }

  if (form.send_call_logs_by_whatsapp) {
    const invalid = (form.whatsapp_number_rows || []).find(
      (row) => row.item_text?.trim() && !isValidMobile10(row.item_text),
    );
    if (invalid) return "One or more WhatsApp numbers are invalid.";
  }

  return null;
}

export function validateOrgBusinessQueryPreferences(form = {}) {
  if (form.send_business_queries_by_email) {
    const invalid = (form.business_query_email_rows || []).find(
      (row) => row.item_text?.trim() && !isValidEmail(row.item_text),
    );
    if (invalid) return "One or more business query email addresses are invalid.";
  }

  if (form.send_business_queries_by_whatsapp) {
    const invalid = (form.business_query_whatsapp_rows || []).find(
      (row) => row.item_text?.trim() && !isValidMobile10(row.item_text),
    );
    if (invalid) return "One or more business query WhatsApp numbers are invalid.";
  }

  return null;
}

function applyCallLogNotificationInput(input, form) {
  const sendEmail = !!form.send_call_logs_by_email;
  const sendWhatsapp = !!form.send_call_logs_by_whatsapp;

  input.send_call_logs_by_email = sendEmail;
  input.send_call_logs_by_whatsapp = sendWhatsapp;

  input.email_addresses = sendEmail
    ? itemRowsToStrings(form.email_address_rows || [])
    : [];

  input.whatsapp_numbers = sendWhatsapp
    ? itemRowsToMobileStrings(form.whatsapp_number_rows || [])
    : [];
}

// Dashboard Digest -- periodic KPI + agent-activity email/WhatsApp summary,
// off by default. Unlike call-log delivery's row-based email/whatsapp
// inputs above, DashboardDigestSettings.vue already hands back a plain
// { send_dashboard_digest_by_email, dashboard_digest_email_addresses, ... }
// object directly (its own v-model contract), so this is a straight
// pass-through rather than another itemRowsToStrings conversion.
function applyDashboardDigestInput(input, form) {
  input.send_dashboard_digest_by_email = !!form.send_dashboard_digest_by_email;
  input.dashboard_digest_email_addresses = form.send_dashboard_digest_by_email
    ? form.dashboard_digest_email_addresses || []
    : [];
  input.send_dashboard_digest_by_whatsapp = !!form.send_dashboard_digest_by_whatsapp;
  input.dashboard_digest_whatsapp_numbers = form.send_dashboard_digest_by_whatsapp
    ? form.dashboard_digest_whatsapp_numbers || []
    : [];
  input.dashboard_digest_cc_agents = !!form.dashboard_digest_cc_agents;
  input.dashboard_digest_times = form.dashboard_digest_times || [];
  // Independent weekly/monthly cadences alongside the daily times above.
  input.dashboard_digest_weekly_enabled = !!form.dashboard_digest_weekly_enabled;
  input.dashboard_digest_weekly_day = form.dashboard_digest_weekly_day || null;
  input.dashboard_digest_weekly_time = form.dashboard_digest_weekly_time || null;
  input.dashboard_digest_monthly_enabled = !!form.dashboard_digest_monthly_enabled;
  input.dashboard_digest_monthly_day = form.dashboard_digest_monthly_day || null;
  input.dashboard_digest_monthly_time = form.dashboard_digest_monthly_time || null;
}

export function buildUpdateOrganizationInput(form, sessionContext) {
  void sessionContext;
  const input = {};

  if (form.org_name?.trim()) input.org_name = form.org_name.trim();
  if (form.org_tag_line?.trim()) {
    input.org_tag_line = form.org_tag_line.trim().slice(0, ORG_TAG_LINE_MAX);
  }
  if (form.org_description?.trim()) {
    input.org_description = form.org_description
      .trim()
      .slice(0, ORG_DESCRIPTION_MAX);
  }
  if (form.org_contact_number?.trim()) {
    input.org_contact_number = form.org_contact_number.trim();
  }
  if (form.org_country_code?.trim()) {
    input.org_country_code = form.org_country_code.trim();
  }
  if (form.org_address?.trim()) input.org_address = form.org_address.trim();
  if (form.org_city?.trim()) input.org_city = form.org_city.trim();
  if (form.org_state?.trim()) input.org_state = form.org_state.trim();
  if (form.org_pincode?.trim()) input.org_pincode = form.org_pincode.trim();

  const logoKey = String(form.logo_s3key ?? form.org_logo_s3_key ?? "").trim();
  if (logoKey) input.org_logo_s3_key = logoKey;

  // Call-log delivery and Dashboard Digest are only meaningful to send when
  // the caller's form actually carries them -- UpdateOrgDetailsDialog.vue's
  // own form always does (real edited values, or the org's current values
  // resent unchanged), but UpdateKnowledgeBaseDialog.vue's much narrower
  // form (org_id/org_name/org_knowledeg_base only) never sets any of these
  // keys at all. Calling either apply*Input helper unconditionally was
  // silently overwriting the org's real saved call-log/digest settings to
  // their JS-falsy defaults (false/[]) on EVERY Knowledge Base save --
  // same "only touch what was sent" reasoning restrict_incoming_calls
  // already uses just above, just missing here until now.
  if (form.send_call_logs_by_email !== undefined || form.send_call_logs_by_whatsapp !== undefined) {
    applyCallLogNotificationInput(input, form);
  }
  if (
    form.send_dashboard_digest_by_email !== undefined ||
    form.send_dashboard_digest_by_whatsapp !== undefined ||
    form.dashboard_digest_cc_agents !== undefined ||
    form.dashboard_digest_times !== undefined ||
    form.dashboard_digest_weekly_enabled !== undefined ||
    form.dashboard_digest_monthly_enabled !== undefined
  ) {
    applyDashboardDigestInput(input, form);
  }

  if (typeof form.restrict_incoming_calls === "boolean") {
    input.restrict_incoming_calls = form.restrict_incoming_calls;
  }

  if (form.greeting_message?.trim()) {
    input.greeting_message = form.greeting_message.trim();
  }
  if (form.system_prompt?.trim()) {
    input.system_prompt = form.system_prompt.trim();
  }

  if (form.chatbot_details != null) {
    input.chatbot_details = form.chatbot_details;
  }

  return sanitizeUpdateOrgProfileInput(input);
}

export default {
  methods: {
    async updateOrganization(input) {
      const payload = sanitizeUpdateOrgProfileInput(input);

      try {
        const { data, errors } = await client.graphql({
          query: updateOrgProfileMutation,
          variables: { input: payload },
        });

        const parsed = parseAwsJson(data?.updateOrgProfile);
        if (parsed != null) {
          return parsed;
        }

        if (errors?.length) {
          throw new Error(errors[0].message);
        }

        return null;
      } catch (error) {
        return {
          status: "ERROR",
          message: getApiMessage(error, "Failed to update organisation"),
        };
      }
    },
  },
};
